import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Rnd, RndResizeCallback } from 'react-rnd';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/authContext';
import { Widget } from '../UserGroupsPage';

import {
  BoxVideoWrapper,
  Cards,
  CloseBtn,
  DragHandle,
  HeaderWilkommen,
  RadialContent,
  WilkommenText,
} from './styles';
import HomePageCard from '../../components/cards/homePageCard';
import RadialMenuReact from '../../components/radialMenu';
import { resolveBreadcrumbColor } from '../../components/radialMenu/radialMenu.js';
import UsergroupBar from '../../components/UsergroupBar';
import Breadcrumb, { Item } from '../../components/breadcrumb';

interface Props {
  widgets?: Widget;
}

const InitialPage: React.FC<Props> = ({ widgets = undefined }) => {
  const videoWidth = 600;
  const videoHeight = 400;
  const videoMargin = 25;

  const authContext = useAuth();
  const { user } = authContext;
  const location = useLocation();

  const [selectedWidget, setSelectedWidget] = useState<Widget>();
  const [showVideo, setShowVideo] = useState<boolean>(true);
  const [widgetsList, setWidgetsList] = useState<Widget[]>(
    user.selectedUsergroup
      ? (user.selectedUsergroup.settings?.widgets?.children ?? [])
      : [],
  );

  const homeButton = location.state?.fromHomeButton ?? false;
  const backButton = location.state?.fromBackButton ?? false;

  const hasClickEvent = !homeButton && !backButton && !!user.selectedUsergroup;
  const [breadcrumbPath, setBreadcrumbPath] = useState<Item[]>(
    user.selectedUsergroup
      ? !homeButton
        ? JSON.parse(localStorage.getItem('breadcrumb') || '[]')
        : []
      : [],
  );
  const [visible, setVisible] = useState<string>('');
  const [isPreviewPage, setIsPreviewPage] = useState<boolean>(false);
  const [rect, setRect] = useState({
    x: Math.max(window.innerWidth - videoWidth - videoMargin, videoMargin),
    y: videoMargin,
    width: videoWidth,
    height: videoHeight,
  });

  const handleResize: RndResizeCallback = (
    _e: any,
    _dir: any,
    ref: any,
    _delta: any,
    position: any,
  ) => {
    setRect({
      width: parseInt(ref.style.width, 10),
      height: parseInt(ref.style.height, 10),
      ...position,
    });
  };

  const handleFirstClick = useCallback(() => {
    setVisible('out');

    window.removeEventListener('click', handleFirstClick);
  }, []);

  const path = useRef<string[]>(
    backButton ? breadcrumbPath.map((step: any) => step.id) : [],
  );

  useEffect(() => {
    if (hasClickEvent) {
      window.addEventListener('click', handleFirstClick);
      setVisible('in');
    }

    if (widgets) {
      // PREVIEW FROM USERGROUPS
      setIsPreviewPage(true);
      setWidgetsList(widgets.children ?? []);
    } else if (user.selectedUsergroup) {
      setWidgetsList(user.selectedUsergroup.settings?.widgets?.children ?? []);
    }
    // user.selectedUsergroup is intentionally tracked by userGroupId (stable)
    // instead of the object reference: AccessLayer's getUserGroups mutates
    // the reference on every navigation when user has 1 usergroup, which
    // would otherwise rebuild the radial menu and lose the open submenu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleFirstClick,
    hasClickEvent,
    user.selectedUsergroup?.userGroupId,
    widgets,
  ]);

  const updateBreadcrumb = useCallback((item: Item, clear = false) => {
    setBreadcrumbPath((prev: any) => {
      if (clear) {
        localStorage.setItem('breadcrumb', JSON.stringify([]));
        return [];
      }

      const hasItem = prev.find((i: any) => i.depth === item.depth);

      let newPath = [];
      if (!hasItem) {
        // insert
        newPath = [
          ...prev,
          { depth: item.depth, text: item.text, id: item.id },
        ];
        localStorage.setItem('breadcrumb', JSON.stringify(newPath));
        return newPath;
      }

      if (item.text && hasItem.text !== item.text) {
        // if open a brother(other item with same level), remove the old and add the new
        newPath = [
          ...prev.filter((i: any) => i.depth < item.depth),
          { depth: item.depth, text: item.text, id: item.id },
        ];
        localStorage.setItem('breadcrumb', JSON.stringify(newPath));
        return newPath;
      }

      // remove
      newPath = prev.filter((i: any) => i.depth < item.depth);
      localStorage.setItem('breadcrumb', JSON.stringify(newPath));
      return newPath;
    });
  }, []);

  const capitalizeFirstLetter = (str?: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <>
      <UsergroupBar
        disabled={isPreviewPage}
        shouldHighlightUserGroups={true}
        updateBreadcrumb={updateBreadcrumb}
        onChangeUsergroup={() => {
          setSelectedWidget(undefined);
          // The saved path belongs to the previous usergroup's widgets;
          // replaying it on the new group's menu would click ids that
          // don't exist there.
          path.current = [];
        }}
      >
        <>
          <Breadcrumb path={breadcrumbPath} />

          <HeaderWilkommen>
            <WilkommenText
              hidden={visible === ''}
              $visible={visible}
            >{`Schön, dass du hier bist ${capitalizeFirstLetter(user?.name)}!`}</WilkommenText>
          </HeaderWilkommen>

          {user.selectedUsergroup?.settings.videoLink && showVideo && (
            <Rnd
              {...rect}
              position={{ x: rect.x, y: rect.y }} // 👈 controla a posição
              size={{ width: rect.width, height: rect.height }}
              bounds="window"
              minWidth={220}
              minHeight={125}
              id="box-video"
              onDragStop={(_, d) => setRect(r => ({ ...r, x: d.x, y: d.y }))}
              onResizeStop={handleResize}
              style={{ zIndex: 9999 }}
              dragHandleClassName="video-drag-handle"
              cancel=".video-close-btn"
            >
              <BoxVideoWrapper>
                <DragHandle className="video-drag-handle">
                  <div style={{ marginTop: '24px' }}>
                    <DragIndicatorOutlinedIcon />
                  </div>
                </DragHandle>
                <CloseBtn
                  onTouchStart={e => e.stopPropagation()}
                  onClick={() => setShowVideo(false)}
                >
                  ×
                </CloseBtn>

                <iframe
                  // width="355"
                  // height="200"
                  // src="https://www.youtube.com/embed/1PnbZdjo5YE"
                  // src="https://victor-bucket.fra1.cdn.digitaloceanspaces.com/NPS%20Erkla%CC%88rung.mp4"
                  // src={userLocal.selectedUsergroup?.settings.videoLink}
                  src={user.selectedUsergroup?.settings.videoLink}
                  title="Avatar"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{ width: '100%', height: '100%' }}
                />
              </BoxVideoWrapper>
            </Rnd>
          )}

          <RadialContent key={user?.selectedUsergroup?.userGroupId}>
            <RadialMenuReact
              setSelectedWidget={setSelectedWidget}
              widgets={widgetsList}
              updateBreadcrumb={updateBreadcrumb}
              path={path.current}
            />
            {selectedWidget && (
              <Cards id="cards">
                <HomePageCard
                  widget={selectedWidget}
                  categoryColor={resolveBreadcrumbColor(
                    [...breadcrumbPath]
                      .sort((a, b) => a.depth - b.depth)
                      .map(item => item.text),
                  )}
                />
              </Cards>
            )}
          </RadialContent>
        </>
      </UsergroupBar>
    </>
  );
};

export default InitialPage;
